import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Settings } from 'lucide-react';
import { API_URL } from '../../services/api';

const TYPES_BIJOU = ['BAGUE', 'COLLIER', 'BRACELET', 'BOUCLE OREILLE', 'PENDENTIF', 'ALLIANCE', 'CHEVALIERE', 'GOURMETTE', 'BROCHE', 'AUTRE'];
const COULEURS_OR = ['JAUNE', 'BLANC', 'ROSE', 'GRIS', 'BICOLORE'];

export default function EditOrdreModal({ ordre, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ordre) return;
    setForm({
      type_bijou:           ordre.type_bijou || '',
      couleur_or:           ordre.couleur_or || '',
      client_nom:           ordre.client_nom || '',
      description:          ordre.description || '',
      notes:                ordre.notes || '',
      date_livraison_prevue: ordre.date_livraison_prevue
        ? new Date(ordre.date_livraison_prevue).toISOString().split('T')[0]
        : '',
      poids_metal:          ordre.poids_metal || '',
      poids_unitaire:       ordre.poids_unitaire || '',
      quantite_pieces:      ordre.quantite_pieces || 1,
      prix_devis:           ordre.prix_devis || '',
      collection_nom:       ordre.collection_nom || '',
      ref_article:          ordre.ref_article || '',
      moule_numero:         ordre.moule_numero || '',
      genre:                ordre.genre || '',
      rhodiage:             ordre.rhodiage ? 1 : 0,
      ecrin:                ordre.ecrin ? 1 : 0,
      gravure_beldi:        ordre.gravure_beldi ? 1 : 0,
      garantie:             ordre.garantie ? 1 : 0,
      consigne:             ordre.consigne || '',
    });
  }, [ordre]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/atelier/ordres-fabrication/${ordre.id}/modifier`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erreur lors de la sauvegarde');
        return;
      }
      onSaved(data.ordre);
      onClose();
    } catch (e) {
      setError('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  if (!ordre) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Settings className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Modifier l'ordre — {ordre.numero_ordre}
              </h2>
              <p className="text-xs text-gray-500">Modifications avant lancement en production</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Corps scrollable ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Bijou */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Bijou</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de bijou</label>
                <select
                  value={form.type_bijou}
                  onChange={e => set('type_bijou', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                >
                  {TYPES_BIJOU.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur / Métal</label>
                <select
                  value={form.couleur_or}
                  onChange={e => set('couleur_or', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                >
                  {COULEURS_OR.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <input
                  type="text"
                  value={form.client_nom}
                  onChange={e => set('client_nom', e.target.value)}
                  placeholder="Nom du client"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Réf. article</label>
                <input
                  type="text"
                  value={form.ref_article}
                  onChange={e => set('ref_article', e.target.value)}
                  placeholder="Ex: COLJ24"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection</label>
                <input
                  type="text"
                  value={form.collection_nom}
                  onChange={e => set('collection_nom', e.target.value)}
                  placeholder="Nom de la collection"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
                <select
                  value={form.genre}
                  onChange={e => set('genre', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                >
                  <option value="">Non défini</option>
                  <option value="FEMME">Femme</option>
                  <option value="HOMME">Homme</option>
                  <option value="ENFANT">Enfant</option>
                  <option value="MIXTE">Mixte</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  rows={2}
                  placeholder="Description du bijou..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Note importante */}
          <div>
            <label className="block text-sm font-medium text-red-700 mb-1 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              Note importante (visible en rouge sur la fiche)
            </label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder="Ex: Taille 54, gravure intérieure, attention rhodiage renforcé..."
              className="w-full border-2 border-red-300 bg-red-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:outline-none"
            />
          </div>

          {/* Production */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Production</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité pièces</label>
                <input
                  type="number" min={1}
                  value={form.quantite_pieces}
                  onChange={e => set('quantite_pieces', parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids OR / pièce (g)</label>
                <input
                  type="number" step="0.001" min={0}
                  value={form.poids_unitaire}
                  onChange={e => set('poids_unitaire', e.target.value)}
                  placeholder="0.000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids brut métal (g)</label>
                <input
                  type="number" step="0.001" min={0}
                  value={form.poids_metal}
                  onChange={e => set('poids_metal', e.target.value)}
                  placeholder="0.000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moule N°</label>
                <input
                  type="text"
                  value={form.moule_numero}
                  onChange={e => set('moule_numero', e.target.value)}
                  placeholder="Ex: M-042"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date livraison</label>
                <input
                  type="date"
                  value={form.date_livraison_prevue}
                  onChange={e => set('date_livraison_prevue', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix devis (DH)</label>
                <input
                  type="number" step="0.01" min={0}
                  value={form.prix_devis}
                  onChange={e => set('prix_devis', e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Options */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Options finition</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: 'rhodiage', label: 'Rhodiage' },
                { key: 'ecrin', label: 'Écrin' },
                { key: 'gravure_beldi', label: 'Gravure beldi' },
                { key: 'garantie', label: 'Garantie' },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2.5 cursor-pointer p-3 rounded-xl border-2 transition ${
                    form[key] ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!form[key]}
                    onChange={e => set(key, e.target.checked ? 1 : 0)}
                    className="accent-orange-500 w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Consigne spéciale</label>
              <input
                type="text"
                value={form.consigne}
                onChange={e => set('consigne', e.target.value)}
                placeholder="Instructions particulières pour l'atelier..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
              />
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          )}
          <div className="flex gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold shadow transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
